import { Suspense } from 'react';

import { Navigate, Outlet } from 'react-router';

import { config } from '@/config';
import { selectIsAdmin, selectIsAuthenticated, useSignOut } from '@/features/auth';
import { SuspensionBanner } from '@/features/billing';
import { useTheme } from '@/lib';
import { useIdleTimer } from '@/lib/hooks/useIdleTimer';
import { PageSkeleton } from '@/lib/ui/molecules';
import { ROUTES } from '@/routes';
import { useAppSelector } from '@/store/hooks';

import { CommandPalette } from './CommandPalette';
import { Footer } from './Footer';
import { GlobalBanner } from './GlobalBanner';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const IDLE_TIMEOUT_MS = 120 * 60 * 1000; // = 2 hours.

export function AdminLayout() {
  const theme = useTheme();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAdmin = useAppSelector(selectIsAdmin);
  const signOut = useSignOut();

  useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle: signOut,
  });

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!isAdmin) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.50]"
        style={{
          backgroundImage:
            theme.resolvedTheme === 'dark'
              ? "url('/network-bg-desktop.png')"
              : "url('/network-bg-desktop-dark.png')",
        }}
        aria-hidden="true"
      />

      {config.appEnv === 'development' && (
        <GlobalBanner>
          <span className="font-semibold">SANDBOX ENVIRONMENT</span> — You&apos;re on the Moveris
          test site. This is not a live environment — actions taken here have no effect on the
          production environment.
        </GlobalBanner>
      )}

      <div className="mx-auto flex min-h-screen w-full max-w-[1920px]">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Topbar />
          <SuspensionBanner />
          <main className="min-h-0 flex-1 overflow-y-auto pb-20 sm:pb-0">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </main>
          <Footer />
        </div>
      </div>
      <CommandPalette isAdmin />
    </div>
  );
}
